// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
  }
}

window.dataLayer = window.dataLayer || []

// ============================================================================
// Type Definitions - Provides autocomplete while allowing any string
// ============================================================================

// Suggested categories from documentation - but allows any string
type SuggestedCategory =
  | '_Think "noun" - What part of the app?'
  //
  | 'Pre-Visit - Quotation' // El paciente solicita cotización.
  | 'Pre-Visit - Scheduling' // El paciente solicita y confirma su cita médica.
  | 'Appointment - Check-in' // El paciente inicia-completa el proceso de registro.
  | 'Appointment - Waiting time' // Tiempo de espera antes de ser atendido
  | 'Appointment - Intake' // Triage/Recepción: Toma los datos iniciales y el motivo de la consulta.
  | 'Appointment - Consultation' // El médico atiende directamente al paciente.
  | 'Appointment - Assessment' // El médico explica el procedimiento, tratamiento, exámenes, y el paciente da su consentimiento para continuar.
  | 'Appointment - Decision-making' // Con la información recopilada tener un posible diagnóstico y un plan de acción.
  | 'Post-Appointment - Instructions' // El médico da instrucciones, plan de tratamiento, recetas de medicamentos y pasos a seguir.
  | 'Post-Appointment - Tests' // El paciente realiza análisis de laboratorio, radiografías u otras pruebas solicitadas.
  | 'Post-Appointment - Results delivery' // Los resultados de pruebas se comunican al paciente.
  | 'Post-Appointment - Follow-up visit' // Cita para revisar resultados de exámenes y confirmar/ajustar el diagnóstico y el tratamiento.
  | 'Post-Appointment - Medical record update' // Información del paciente actualizada en su expediente médico.
  | 'Post-Appointment - Ongoing care' // El ciclo concluye; los datos se integran al historial médico del paciente.
  | 'Post-Appointment - Check-out' // Pago y coordinación de citas futuras.
  //
  | 'Appointment-Management'
  | 'Billing'
  | 'Client-Management'
  | 'Patient-Management'
  //
  | 'Performance'
  | 'API Request'
  //
  | 'Notifications'
  //
  | 'Errors'
  | 'Navigation'
  | 'Authentication'
  //
  | 'Pages'
  | 'Component'
  | 'Forms'
  | 'Media'
  | 'Reports'
  //
  | 'Lab Results'
  | 'Prescriptions'
  | 'Search'
  | 'Settings'
  | 'System'
  | 'Version'

// Suggested actions from documentation - but allows any string
type SuggestedAction =
  | '_Use clear, action-oriented verbs'
  //
  | 'Back'
  | 'Cancel'
  | 'Click'
  | 'Close'
  | 'Collapse'
  | 'Create'
  | 'Delete'
  | 'Download'
  | 'Expand'
  | 'Export'
  | 'Filter'
  | 'Generate'
  | 'Import'
  | 'Login'
  | 'Logout'
  | 'Next'
  | 'Open'
  | 'Password Change'
  | 'Password Reset'
  | 'Save'
  | 'Select'
  | 'Signup'
  | 'Start'
  | 'Submit'
  | 'Switch Tab'
  | 'Sort'
  | 'Toggle'
  | 'Update'
  | 'Upload'
  //
  | 'Error'
  | 'Navigate'
  | 'Search'
  | 'View'

// Suggested label patterns from documentation - but allows any string
type SuggestedLabel =
  | '_NO PII (names, emails, IDs)'
  | '_Pattern: [Context] - [Element Name]'
  //
  | 'Close - button'
  | 'Open - button'
  | 'Report - Export PDF button'

// Helper type: suggests specific strings but allows any string
type StringWithAutocomplete<T extends string> = T | (string & {})

// Additional parameters that can be passed to trackEvent
// Most exist in GA4.
// 1. Data needs to be sent before registering.
// 2. Wait for 1 day.
// 3. Go to GA4 → Admin (⚙️) → Custom definitions.
// 4. Click "Create custom dimension" and select from the list.
// 5. Wait for a new day to start seeing data (prev. data will be lost).
interface AdditionalEventParams {
  client_id?: string
  format?: string
  length?: number
  transaction_id?: string
  value?: number

  file_format?: number
  file_size?: number

  // Performance metrics
  latency?: number
  load_time?: number
  response_generation_time?: number
  response_time?: number

  // Technical context
  error_code?: string
  error_type?: string
  http_method?: string
  http_status?: number
  retry_attempted?: boolean
  retry_count?: number

  // Non-interaction flag
  non_interaction?: boolean

  // Allow any other custom parameters
  [key: string]: unknown
}

// Event data structure pushed to dataLayer
interface EventData extends AdditionalEventParams {
  event: string
  event_action: string
  event_category: string
  event_label: string
  eventAction: string
  eventCategory: string
  eventLabel: string
}

// ============================================================================
// Core trackEvent Function
// ============================================================================

/**
 * Enhanced function to send events to Google Tag Manager + Google Analytics GA4
 *
 * @param category - The type of interaction (e.g., "Navigation", "Patient Management")
 * @param action - What happened (e.g., "Click", "Submit", "Delete")
 * @param label - Specific element with context (e.g., "Patient Form - Save Button")
 * @param additionalParams - Optional additional parameters for GA4
 *
 * @example
 * trackEvent('Patient Management', 'Create', 'New Patient Button')
 * trackEvent('Forms', 'Submit', 'Patient Intake Form', { form_fields: 12 })
 */
export const trackEvent = (
  category: StringWithAutocomplete<SuggestedCategory>,
  action: StringWithAutocomplete<SuggestedAction>,
  label: StringWithAutocomplete<SuggestedLabel>,
  additionalParams: AdditionalEventParams = {},
): void => {
  // GA4-compatible event structure
  const eventData: EventData = {
    event: 'custom_event', // GTM trigger event name
    event_action: action, // GA4 parameter
    event_category: category, // GA4 parameter
    event_label: label, // GA4 parameter
    // Legacy UA parameters for backward compatibility (optional)
    eventAction: action,
    eventCategory: category,
    eventLabel: label,
    ...additionalParams, // Spread any additional parameters
  }

  window.dataLayer.push(eventData)

  // Enhanced development logging
  if (process.env.NODE_ENV === 'development') {
    const colorParts = [category.charCodeAt(0) || 0, category.charCodeAt(1) || 0, category.charCodeAt(2) || 0].map(
      (c) => (c * 4) % 256,
    )
    const actionParts = ['act'.charCodeAt(0) || 0, 'act'.charCodeAt(1) || 0, 'act'.charCodeAt(2) || 0].map(
      (c) => (c * 3) % 256,
    )
    const labelParts = [label.charCodeAt(0) || 0, label.charCodeAt(1) || 0, label.charCodeAt(2) || 0].map(
      (c) => (c * 2) % 256,
    )
    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `%c%s%c%s%c%s%c%s%c%s%c%s`,
      'color:#4c587b;font-weight:normal;margin-right:4px',
      '📊 Analytics Event:',
      `color:#191f38;border:1px solid rgba(${colorParts.toString()}, 0.7);padding:2px 5px;border-radius:4px;font-weight:normal;background-color:rgba(${colorParts.toString()}, 0.15);margin:0 2px`,
      category,
      `color:#191f38;font-weight:normal;margin:0 4px;font-size:1.5em; line-height:0.75em`,
      '»',
      `color:#191f38;border:1px solid rgba(${actionParts.toString()}, 0.7);padding:2px 5px;border-radius:4px;font-weight:normal;background-color:rgba(${actionParts.toString()}, 0.15);margin:0 2px`,
      action,
      `color:#191f38;font-weight:normal;margin:0 4px;font-size:1.5em; line-height:0.75em`,
      '»',
      `color:#191f38;border:1px solid rgba(${labelParts.toString()}, 0.7);padding:2px 5px;border-radius:4px;font-weight:normal;background-color:rgba(${labelParts.toString()}, 0.15);margin:0 2px`,
      label,
    )
    // eslint-disable-next-line no-console
    console.log('Event Data Pushed to dataLayer:', eventData)
    // eslint-disable-next-line no-console
    console.log('Current dataLayer:', window.dataLayer)
    // eslint-disable-next-line no-console
    console.groupEnd()
  }
}
